import React, { useState } from "react";
import { useSnackbar } from "notistack";
import { Button, DialogActions, DialogContent, DialogTitle, LinearProgress, Grid, FormControl, Typography, MenuItem, Select, TextField, Dialog, useMediaQuery, Theme, Hidden, InputLabel } from "@material-ui/core";
import moment from "moment";
import { getFeedbackForm, patchFeedbackForm, SipFeedbackForm, exportFeedbackForm } from "api/admin/sip";
import { Booking } from "api/bookings";
import i18n from "./i18n";

// prop types
interface SIPFormDialogProps {
    booking?: Booking,
    show: boolean,
    onClose: () => void,
}

// age groups
const ageGroups = ["kids", "youngAdults", "adults", "seniors", "unknownAge", "disabled"] as const;

// genders
const genders = ["m", "f", "d", "u"] as const;

// weather conditions
const weatherConditions = ["sunny", "cloudy", "veryCloudy", "rainy", "veryRainy"] as const;

// edit SIP form dialog
const SIPFormDialog = ({ booking, show, onClose } : SIPFormDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
    const [sipData, setSIPData] = useState<SipFeedbackForm>();

    // fetch sip data
    const fetchData = async () => {
        setShowLoadingAnimation(true);
        try {
            setSIPData(await getFeedbackForm({ bookingId: booking!.id }));
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // save sip data
    const saveData = async () => {
        setShowLoadingAnimation(true);
        try {
            await patchFeedbackForm(sipData!);
            onClose();
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // export sip form data
    const exportData = async () => {
        setShowLoadingAnimation(true);
        try {
            const { url } = await exportFeedbackForm({ bookingId: booking!.id });
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // did mount hook -> fetch data
    React.useEffect(() => { if (booking) fetchData(); }, [booking]); // eslint-disable-line

    // change participants count
    const setParticipantsCount = (ageGroup: typeof ageGroups[number], gender: typeof genders[number], count: number) => {
        if (count < 0) return;
        setSIPData({
            ...sipData!,
            formData: {
                ...sipData!.formData,
                [ageGroup]: {
                    ...sipData!.formData[ageGroup],
                    [gender]: count,
                },
            },
        });
    };

    // render participants row
    const renderParticipantsRow = (ageGroup: typeof ageGroups[number]) => (
        <Grid container alignItems="center" spacing={1} key={ageGroup}>
            <Grid item sm={4} xs={12}>
                <Typography>{i18n.ageGroups[ageGroup]}</Typography>
            </Grid>
            {genders.map((gender) => (
                <Grid item sm={2} xs={6} key={gender}>
                    <TextField
                        label={i18n.genders[gender]}
                        placeholder="0"
                        type="number"
                        value={sipData!.formData[ageGroup][gender]}
                        onBlur={(e) => (e.target.value === "") && setParticipantsCount(ageGroup, gender, 0)}
                        onChange={(e) => setParticipantsCount(ageGroup, gender, parseInt(e.target.value, 10))}
                    />
                </Grid>
            ))}
            <Hidden smUp>
                <Grid item xs={12} style={{ marginBottom: 24 }} />
            </Hidden>
        </Grid>
    );

    // render meta row
    const renderMetaRow = (label: string, content: React.ReactNode) => (
        <>
            <Grid item xs={4}>
                <Typography>{label}</Typography>
            </Grid>
            <Grid item xs={8}>
                {content}
            </Grid>
        </>
    );

    // render
    return (
        <Dialog
            fullScreen={useMediaQuery((theme: Theme) => theme.breakpoints.down("xs"))}
            maxWidth="lg"
            open={show}
            onClose={onClose}
        >
            <DialogTitle>{i18n.sipFormLong}</DialogTitle>
            <DialogContent dividers>

                {/* booking meta */}
                <FormControl fullWidth margin="normal">
                    <Typography variant="subtitle2" gutterBottom>{i18n.general}</Typography>
                    <Grid container spacing={1}>
                        {renderMetaRow(i18n.date, (
                            <Typography>{moment(booking?.startDate).format("DD.MM.YYYY")}</Typography>
                        ))}
                        {renderMetaRow(i18n.time, (
                            // eslint-disable-next-line react/jsx-one-expression-per-line
                            <Typography>{moment(booking?.startDate).format("HH:mm")}{" - "}{moment(booking?.endDate).format("HH:mm")}</Typography>
                        ))}
                        {renderMetaRow(i18n.club, (
                            <Typography>{booking?.court.clubName}</Typography>
                        ))}
                        <Hidden xsDown>
                            {renderMetaRow(i18n.weatherConditions, (
                                <Select
                                    value={sipData?.weatherCondition || ""}
                                    onChange={(e) => setSIPData({ ...sipData!, weatherCondition: e.target.value as SipFeedbackForm["weatherCondition"] })}
                                >
                                    {weatherConditions.map((c) => <MenuItem key={c} value={c}>{i18n.weatherConditionsList[c]}</MenuItem>)}
                                </Select>
                            ))}
                            {renderMetaRow(i18n.temperature, (
                                <TextField
                                    placeholder="0"
                                    type="number"
                                    value={sipData?.temperatureDegC || ""}
                                    onChange={(e) => setSIPData({ ...sipData!, temperatureDegC: parseFloat(e.target.value) })}
                                />
                            ))}
                            {renderMetaRow(i18n.relHumidity, (
                                <TextField
                                    inputProps={{ step: 1, min: 0, max: 100 }}
                                    placeholder="0"
                                    type="number"
                                    value={sipData?.humidityPercent || ""}
                                    onChange={(e) => setSIPData({ ...sipData!, humidityPercent: parseInt(e.target.value, 10) })}
                                />
                            ))}
                            {booking?.bookingStatus === "cancelled" && renderMetaRow(i18n.cancellationReason, (
                                <TextField
                                    fullWidth
                                    value={sipData?.cancellationReason || ""}
                                    onChange={(e) => {
                                        if (e.target.value.length > 100) return;
                                        setSIPData({ ...sipData!, cancellationReason: e.target.value });
                                    }}
                                />
                            ))}
                        </Hidden>

                        {/* mobile variant */}
                        <Hidden smUp>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>{i18n.weatherConditions}</InputLabel>
                                    <Select
                                        label={i18n.weatherConditions}
                                        value={sipData?.weatherCondition || ""}
                                        onChange={(e) => setSIPData({ ...sipData!, weatherCondition: e.target.value as SipFeedbackForm["weatherCondition"] })}
                                    >
                                        {weatherConditions.map((c) => <MenuItem key={c} value={c}>{i18n.weatherConditionsList[c]}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={i18n.temperature}
                                    placeholder="0"
                                    type="number"
                                    value={sipData?.temperatureDegC || ""}
                                    onChange={(e) => setSIPData({ ...sipData!, temperatureDegC: parseFloat(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    inputProps={{ step: 1, min: 0, max: 100 }}
                                    label={i18n.relHumidity}
                                    placeholder="0"
                                    type="number"
                                    value={sipData?.humidityPercent || ""}
                                    onChange={(e) => setSIPData({ ...sipData!, humidityPercent: parseInt(e.target.value, 10) })}
                                />
                            </Grid>
                            {booking?.bookingStatus === "cancelled" && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={i18n.cancellationReason}
                                        value={sipData?.cancellationReason || ""}
                                        onChange={(e) => {
                                            if (e.target.value.length > 100) return;
                                            setSIPData({ ...sipData!, cancellationReason: e.target.value });
                                        }}
                                    />
                                </Grid>
                            )}
                        </Hidden>
                    </Grid>
                </FormControl>

                {/* participants count table */}
                <FormControl fullWidth margin="normal">
                    <Typography variant="subtitle2" gutterBottom>{i18n.participantsPerAgeGroup}</Typography>
                    {sipData?.formData && ageGroups.map(renderParticipantsRow)}
                </FormControl>

                {/* last edited */}
                <FormControl fullWidth margin="normal">
                    <Typography align="left" variant="subtitle2" gutterBottom>
                        {i18n.lastEdited}
                    </Typography>
                    <Typography align="left" variant="body1">
                        {!sipData?.author ? i18n.never
                            : `${moment(sipData.updatedAt).format("DD.MM.YYYY HH:mm")} ${i18n.by} ${sipData.author.firstName} ${sipData.author.lastName}`}
                    </Typography>
                </FormControl>

            </DialogContent>
            {showLoadingAnimation && (
                <LinearProgress />
            )}
            <DialogActions>
                <Hidden xsDown>
                    <Button onClick={exportData} color="primary" disabled={showLoadingAnimation} style={{ marginRight: "auto" }}>
                        {i18n.export}
                    </Button>
                </Hidden>
                <Button onClick={onClose} color="secondary">
                    {i18n.cancel}
                </Button>
                <Button onClick={saveData} color="primary" disabled={showLoadingAnimation}>
                    {i18n.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SIPFormDialog;
